/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B070010 入荷予定実績照会
 */
import { Box } from "@mui/material";
import { Column, Query } from "@material-table/core";
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  SCodeCheck,
  SFileButton,
  msgState,
} from "components";
import {
  getSCode,
  IchiranhyojiSelect,
  SCode,
  ShohinshubetsuKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, menusState, useFocus } from "states";
import { useNavigate } from "react-router-dom";
import { B000111, B000170, B000200 } from "pages";
import {
  B000111KakoshubetsuNm,
  B000170ShireNm,
  B000200ShohinNm1,
  B000200ByJanCdShohinNm2,
  catchFileError,
  catchRestError,
  B070010Find,
  B070010FindRequest,
  B070010FindResponse,
  B070010GetExcel,
  B070010ExcelRequest,
} from "clients/rest";
import { B000160 } from "pages/b000/B000160";
import { meishoKbn } from "services/codes";
import { date, download, mapIds } from "services";
import { getClientMessage } from "messages";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shireCd: string;
  shireNm: string;
  juchudenpyoNo: string;
  hachudenpyoNo: string;
  shohinCd: string;
  shohinNm1: string;
  janCd: string;
  shohinNm2: string;
  itirahyoji: SCode;
  torikomijilistHako: string;
  crtdateFrom: string;
  crthourFrom: string;
  crtminuteFrom: string;
  crtdateTo: string;
  crthourTo: string;
  crtminuteTo: string;
  nyukayoteiYmdFrom: string;
  nyukayoteiYmdTo: string;
  nyukaYmd: string;
  juchuhinhasoYmdFrom: string;
  juchuhinhasoYmdTo: string;
  kakoshubetsuCd: string;
  kakoshubetsuNm: string;
  shohinshubetsuKbn: SCode;
  nyukayoteisuGokei: string;
  shiresuGokei: string;
  nyukazansuGokei: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  juchudenpyoNoView: string;
  hachudenpyoNoView: string;
  shohinCd: string;
  janCdView: string;
  shohinNm: string;
  nyukayoteiYmdView: string;
  shireYmdView: string;
  nyukayoteisu: string;
  shiresu: string;
  kafusokuRiyuNm: string;
  kakoshubetsuNm: string;
  shainNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shireCd: "",
  shireNm: "",
  juchudenpyoNo: "",
  hachudenpyoNo: "",
  shohinCd: "",
  shohinNm1: "",
  janCd: "",
  shohinNm2: "",
  itirahyoji: getSCode(IchiranhyojiSelect, "0"),
  torikomijilistHako: "0",
  crtdateFrom: "",
  crthourFrom: "",
  crtminuteFrom: "",
  crtdateTo: "",
  crthourTo: "",
  crtminuteTo: "",
  nyukayoteiYmdFrom: date(),
  nyukayoteiYmdTo: date(),
  nyukaYmd: date(),
  juchuhinhasoYmdFrom: "",
  juchuhinhasoYmdTo: "",
  kakoshubetsuCd: "",
  kakoshubetsuNm: "",
  shohinshubetsuKbn: undefined,
  nyukayoteisuGokei: "",
  shiresuGokei: "",
  nyukazansuGokei: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  {
    field: "juchudenpyoNoView",
    title: "受注No.",
    width: "10rem",
    align: "left",
  },
  {
    field: "hachudenpyoNoView",
    title: "発注No.",
    width: "9rem",
  },
  { field: "shohinCd", title: "商品コード", width: "8rem" },
  { field: "janCdView", title: "JANコード", width: "10rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  { field: "nyukayoteiYmdView", title: "入荷予定日", width: "8rem" },
  { field: "shireYmdView", title: "入荷日", width: "8rem" },
  {
    field: "nyukayoteisu",
    title: "入荷予定数　",
    width: "5rem",
    align: "right",
    render: (data) => data.nyukayoteisu?.toLocaleString(),
  },
  {
    field: "shiresu",
    title: "入荷数　",
    width: "5rem",
    align: "right",
    render: (data) => data.shiresu?.toLocaleString(),
  },
  { field: "kafusokuRiyuNm", title: "過不足理由", width: "20rem" },
  { field: "kakoshubetsuNm", title: "加工種別", width: "8rem" },
  { field: "shainNm", title: "入力担当者", width: "8rem" },
  { field: "gyoNo", hidden: true },
];

const focusId = "B070010";

// B070010 入荷予定実績照会
export const B070010 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 1ページの行数
  const pageSize = 15;
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 一覧表示選択が「入荷商品」かどうか
  const [isNyukashohin, setIsNyukashohin] = useState(false);
  // 検索結果が0件かどうか
  const [isSearchResultZero, setIsSearchResultZero] = useState(true);
  // menusステート取得
  const menus = useRecoilValue(menusState);
  // 画面ID
  const gamenId = "040020";
  // 営業所権限取得
  const eigyosyoKengen = menus["040"].pages[gamenId].eigyosyoKengen;

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    const fields = methods.getValues();
    // 登録時(From)チェック
    if (fields.crthourFrom) {
      if (Number(fields.crthourFrom) >= 24 || Number(fields.crthourFrom) < 0) {
        setMsg("登録日時の範囲指定「時間(開始)」が存在しない時刻です。");
        return;
      }
    }

    // 登録分(From)チェック
    if (fields.crtminuteFrom) {
      if (
        Number(fields.crtminuteFrom) >= 60 ||
        Number(fields.crtminuteFrom) < 0
      ) {
        setMsg("登録日時の範囲指定「分(開始)」が存在しない時刻です。");
        return;
      }
    }

    // 登録時(To)チェック
    if (fields.crthourTo) {
      if (Number(fields.crthourTo) >= 24 || Number(fields.crthourTo) < 0) {
        setMsg("登録日時の範囲指定「時間(終了)」が存在しない時刻です。");
        return;
      }
    }

    // 登録分(To)チェック
    if (fields.crtminuteTo) {
      if (Number(fields.crtminuteTo) >= 60 || Number(fields.crtminuteTo) < 0) {
        setMsg("登録日時の範囲指定「分(終了)」が存在しない時刻です。");
        return;
      }
    }

    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B070010FindRequest = {
        nyukayoteiYmdFrom: fields.nyukayoteiYmdFrom!,
        nyukayoteiYmdTo: fields.nyukayoteiYmdTo!,
        nyukaYmd: fields.nyukaYmd!,
        shireCd: fields.shireCd,
        juchudenpyoNo: fields.juchudenpyoNo,
        hachudenpyoNo: fields.hachudenpyoNo,
        shohinCd: fields.shohinCd,
        janCd: fields.janCd,
        itiranhyoji: fields.itirahyoji?.value,
        nyukayoteilisthakozumiKbn: fields.torikomijilistHako,
        crtdayFrom: fields.crtdateFrom,
        crthourFrom: fields.crthourFrom,
        crtminuteFrom: fields.crtminuteFrom,
        crtdayTo: fields.crtdateTo,
        crthourTo: fields.crthourTo,
        crtminuteTo: fields.crtminuteTo,
        juchuhinhasoYmdFrom: fields.juchuhinhasoYmdFrom,
        juchuhinhasoYmdTo: fields.juchuhinhasoYmdTo,
        kakoshubetsuCd: fields.kakoshubetsuCd,
        shohinshubetsuKbn: fields.shohinshubetsuKbn?.value,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["nyukayoteiYmd,asc", "nyukayoteikanriNo,asc"],
      };
      // API呼び出し
      B070010Find(param)
        // 正常取得時
        .then((value) => {
          console.log(value.data);
          // 合計値の設定
          handleGokei(value.data.content[0], value.data.totalElements);

          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          // 検索結果が0件ステートをtrueにする
          setIsSearchResultZero(true);
          // エラー時は合計値をクリアする。
          methods.setValue("nyukayoteisuGokei", "");
          methods.setValue("shiresuGokei", "");
          methods.setValue("nyukazansuGokei", "");
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 合計値の設定
  const handleGokei = (data: B070010FindResponse, totalElements: number) => {
    // 検索結果が存在するかチェック
    if (totalElements === 0) {
      // データ非存在メッセージ表示
      setMsg("指定された検索条件に合致するデータは存在しません。");
      // 検索結果が0件ステートをtrueにする
      setIsSearchResultZero(true);
      // 結果なしの場合、クリア
      methods.setValue("nyukayoteisuGokei", "");
      methods.setValue("shiresuGokei", "");
      methods.setValue("nyukazansuGokei", "");
    }
    // 結果が1件以上取得できた場合
    else {
      // 検索結果が0件ステートをfalseにする
      setIsSearchResultZero(false);
      // 合計値の設定
      methods.setValue("nyukayoteisuGokei", data.nyukayoteisuGokei ?? "");
      methods.setValue("shiresuGokei", data.shiresuGokei ?? "");
      methods.setValue("nyukazansuGokei", data.nyukazansuGokei ?? "");
    }
  };

  // 一覧表示選択変更時イベント
  const handleChandeitirahyoji = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();

    // 入荷予定->入荷商品 へ切り替え
    if (fields.itirahyoji?.value === "1") {
      setIsNyukashohin(true);
    } else if (fields.itirahyoji?.value === "0") {
      setIsNyukashohin(false);
    }
  };

  // EXCEL出力ボタン
  const handleClickExcelButton = () => {
    // EXCEL出力フォームのデータを取得する。
    const fields = methods.getValues();

    // 登録時(From)チェック
    if (fields.crthourFrom) {
      if (Number(fields.crthourFrom) >= 24 || Number(fields.crthourFrom) < 0) {
        setMsg("登録日時の範囲指定「時間(開始)」が存在しない時刻です。");
        return;
      }
    }

    // 登録分(From)チェック
    if (fields.crtminuteFrom) {
      if (
        Number(fields.crtminuteFrom) >= 60 ||
        Number(fields.crtminuteFrom) < 0
      ) {
        setMsg("登録日時の範囲指定「分(開始)」が存在しない時刻です。");
        return;
      }
    }

    // 登録時(To)チェック
    if (fields.crthourTo) {
      if (Number(fields.crthourTo) >= 24 || Number(fields.crthourTo) < 0) {
        setMsg("登録日時の範囲指定「時間(終了)」が存在しない時刻です。");
        return;
      }
    }

    // 登録分(To)チェック
    if (fields.crtminuteTo) {
      if (Number(fields.crtminuteTo) >= 60 || Number(fields.crtminuteTo) < 0) {
        setMsg("登録日時の範囲指定「分(終了)」が存在しない時刻です。");
        return;
      }
    }

    // EXCEL出力パラメータを作成する。
    const param: B070010ExcelRequest = {
      nyukayoteiYmdFrom: fields.nyukayoteiYmdFrom,
      nyukayoteiYmdTo: fields.nyukayoteiYmdTo,
      nyukaYmd: fields.nyukaYmd,
      shireCd: fields.shireCd,
      juchudenpyoNo: fields.juchudenpyoNo,
      hachudenpyoNo: fields.hachudenpyoNo,
      shohinCd: fields.shohinCd,
      janCd: fields.janCd,
      itiranhyoji: fields.itirahyoji?.value,
      nyukayoteilisthakozumiKbn: fields.torikomijilistHako,
      crtdayFrom: fields.crtdateFrom,
      crthourFrom: fields.crthourFrom,
      crtminuteFrom: fields.crtminuteFrom,
      crtdayTo: fields.crtdateTo,
      crthourTo: fields.crthourTo,
      crtminuteTo: fields.crtminuteTo,
      juchuhinhasoYmdFrom: fields.juchuhinhasoYmdFrom,
      juchuhinhasoYmdTo: fields.juchuhinhasoYmdTo,
      kakoshubetsuCd: fields.kakoshubetsuCd,
      shohinshubetsuKbn: fields.shohinshubetsuKbn?.value,
    };
    B070010GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B070010_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="入荷予定実績照会"
          gridTemplateColumns="6rem 10rem 20rem 1rem 6rem 5rem 3rem 1rem 1rem 1rem 2rem 1rem 4rem 4rem 2rem 1rem 2rem"
        >
          <SLabel>仕入先コード</SLabel>
          <SNumberField
            name="shireCd"
            label="仕入先コード"
            code
            size={6}
            dialog={<B000170 />}
            handleSubmit={(data: any) => {
              methods.setValue("shireCd", data.shireCd);
              methods.setValue("shireNm", data.shireNmr);
            }}
            onBlur={() => B000170ShireNm(methods, navigate, setMsg)}
            {...register(1)}
          />
          <STextField name="shireNm" label="仕入先名" size={24} disabled />
          <Box />
          <SLabel>一覧表示選択</SLabel>
          <SCodeSelect
            name="itirahyoji"
            label="一覧表示選択"
            options={IchiranhyojiSelect}
            onChange={() => handleChandeitirahyoji()}
            gridColumn="span 2"
            {...register(6)}
          />
          <SLabel gridColumn="span 5 ">リスト再発行</SLabel>
          <SCodeCheck
            name="torikomijilistHako"
            label="リスト再発行"
            on={1}
            off={0}
            disabled={isNyukashohin}
            {...register(7)}
          />
          <SLabel gridColumn="1">受注No.</SLabel>
          <SNumberField
            name="juchudenpyoNo"
            label="受注No."
            code
            size={10}
            {...register(2)}
          />
          <Box />
          <SLabel gridColumn="5">登録日時</SLabel>
          <SDateField
            name="crtdateFrom"
            label="登録日時(日付)(From)"
            gridColumn="span 2"
            {...register(8)}
          />
          <SNumberField
            name="crthourFrom"
            label="登録日時(時)(From)"
            size={2}
            gridColumn="span 2"
            {...register(9)}
          />
          <SText>：</SText>
          <SNumberField
            name="crtminuteFrom"
            label="登録日時(分)(From)"
            size={2}
            {...register(10)}
          />
          <SText>～</SText>
          <SDateField
            name="crtdateTo"
            label="登録日時(日付)(To)"
            gridColumn="span 2"
            {...register(11)}
          />
          <SNumberField
            name="crthourTo"
            label="登録日時(時)(To)"
            size={2}
            {...register(12)}
          />
          <SText>：</SText>
          <SNumberField
            name="crtminuteTo"
            label="登録日時(分)(To)"
            size={2}
            {...register(13)}
          />
          <Box />
          <SLabel gridColumn="1">発注No.</SLabel>
          <SNumberField
            name="hachudenpyoNo"
            label="発注No."
            code
            size={10}
            dialog={
              <B000160 eigyosyoKengen={eigyosyoKengen} gamenId={gamenId} />
            }
            handleSubmit={(data: any) => {
              methods.setValue("hachudenpyoNo", data.hachudenpyoNoF);
            }}
            {...register(3)}
          />
          <Box />

          {!isNyukashohin ? (
            <>
              <SLabel gridColumn="5" required>
                入荷予定日
              </SLabel>
              <SDateField
                name="nyukayoteiYmdFrom"
                label="入荷予定日(From)"
                required
                gridColumn="span 2"
                {...register(14)}
              />
              <SText gridColumn="span 2">～</SText>
              <SDateField
                name="nyukayoteiYmdTo"
                label="入荷予定日(To)"
                required
                gridColumn="span 4"
                {...register(15)}
              />
            </>
          ) : (
            <>
              <SLabel gridColumn="5" required>
                入荷日
              </SLabel>
              <SDateField
                name="nyukaYmd"
                label="入荷日"
                required
                gridColumn="span 2"
                {...register(14)}
              />
              <Box gridColumn="span 6" />
            </>
          )}

          <SLabel gridColumn="1">商品コード</SLabel>
          <SNumberField
            name="shohinCd"
            label="商品コード"
            code
            size={10}
            dialog={<B000200 />}
            handleSubmit={(data: any) => {
              methods.setValue("shohinCd", data.shohinCd);
              methods.setValue("shohinNm1", data.shohinNmr);
            }}
            onBlur={() => B000200ShohinNm1(methods, navigate, setMsg)}
            {...register(4)}
          />
          <STextField name="shohinNm1" label="商品名" size={24} disabled />
          <Box />

          {!isNyukashohin ? (
            <>
              <SLabel>受注品発送日</SLabel>
              <SDateField
                name="juchuhinhasoYmdFrom"
                label="受注品発送日(From)"
                gridColumn="span 2"
                {...register(16)}
              />
              <SText gridColumn="span 2">～</SText>
              <SDateField
                name="juchuhinhasoYmdTo"
                label="受注品発送日(To)"
                gridColumn="span 4"
                {...register(17)}
              />
            </>
          ) : (
            <>
              <Box gridColumn="span 9" />
            </>
          )}

          <SLabel gridColumn="1">JANコード</SLabel>
          <SNumberField
            name="janCd"
            label="JANコード"
            code
            size={13}
            dialog={<B000200 />}
            handleSubmit={(data: any) => {
              methods.setValue("janCd", data.janCd);
              methods.setValue("shohinNm2", data.shohinNmr);
            }}
            onBlur={() => B000200ByJanCdShohinNm2(methods, navigate, setMsg)}
            {...register(5)}
          />
          <STextField name="shohinNm2" label="JAN商品名" size={24} disabled />
          <Box />

          {!isNyukashohin ? (
            <>
              <SLabel>加工種別</SLabel>
              <SNumberField
                name="kakoshubetsuCd"
                label="加工種別コード"
                code
                size={3}
                dialog={<B000111 meishoKbn={meishoKbn.kakoshubetsu} />}
                handleSubmit={(data: any) => {
                  methods.setValue("kakoshubetsuCd", data.meishoCd);
                  methods.setValue("kakoshubetsuNm", data.meishoNm1);
                }}
                onBlur={() => B000111KakoshubetsuNm(methods, navigate, setMsg)}
                {...register(18)}
              />
              <STextField
                name="kakoshubetsuNm"
                label="加工種別名"
                gridColumn="span 9"
                size={40}
                disabled
              />
            </>
          ) : (
            <>
              <Box gridColumn="span 11" />
            </>
          )}

          <SLabel gridColumn="5">商品種別</SLabel>
          <SCodeSelect
            name="shohinshubetsuKbn"
            label="商品種別"
            options={ShohinshubetsuKbn}
            empty
            gridColumn="span 5"
            {...register(19)}
          />
          <SNormalButton
            label={labels.buttons.search}
            gridColumn="16"
            gridRow="7"
            align="right"
            onClick={handleClickSearchButton}
            {...register(20)}
          />
        </SFrame>
        <SFrame gridTemplateColumns="57.7rem 7rem 7rem 7rem ">
          <Box />
          <SLabel>入荷予定数合計</SLabel>
          <SLabel>入荷数合計</SLabel>
          <SLabel>入荷残数合計</SLabel>
          <Box />
          <SNumberField
            name="nyukayoteisuGokei"
            label="入荷予定数合計"
            disabled
            align="right"
          />
          <SNumberField
            name="shiresuGokei"
            label="入荷数合計"
            disabled
            align="right"
          />
          <SNumberField
            name="nyukazansuGokei"
            label="入荷残数合計"
            disabled
            align="right"
          />
        </SFrame>
        <SFrame>
          <STable
            keyName="gyoNo"
            data={handleSearch}
            columns={columns}
            tableRef={tableRef}
            fixed={5}
            width={1260}
            height={440}
            pageSize={pageSize}
          />
        </SFrame>
        <SFrame>
          <Box />
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            disabled={isNyukashohin || isSearchResultZero}
            onClick={handleClickExcelButton}
            {...register(21)}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
